<template>
  <div class="pokemon-wrapper">
    <div class="animated fadeIn">
      <component :is="fixedUser ? 'div' : 'b-card'">
        <template #header v-if="!fixedUser">
          <i class="fa fa-list"></i> <strong>Pokemon Database</strong>
          <span v-if="servers[activeServer].readonly" class="badge badge-primary ml-1">Read-only</span>
          <div class="card-actions">
            <div class="btn-group" role="group">
              <template v-for="(item, index) in servers">
                <button type="button" @click="switchServer(index)"
                        :key="index"
                        :class="['btn-' + item.colour, activeServer === index ? 'active' : '']"
                        class="btn btn-small">{{ capitalizeFirstLetter(index) }}
                </button>
              </template>
            </div>
          </div>
        </template>

        <div v-if="!fixedUser" class="bg-dark-subtle border border-secondary rounded p-2 px-3 text-muted small mb-3 mx-1">
          <i class="fa fa-info-circle text-info mr-1"></i>
          <strong class="text-light">Note:</strong> Most non-indexed filters and sorting options are disabled in global search for performance.
          Filtering on <strong>Owner, ID, GID, OT or indexed IVs (SPD, SPATK, ATK)</strong> is enabled globally.
          <span class="text-warning ml-2"><i class="fa fa-exclamation-triangle"></i> Please avoid rapid page refreshes (F5) to reduce server load.</span>
        </div>

        <div class="rounded shadow-sm border overflow-hidden">
          <ag-grid-vue
            style='width: 100%; height: 750px'
            class='ag-theme-balham-dark'
            :gridOptions='gridOptions'
            :defaultColDef="defaultColDef"
            @grid-ready="onGridReady">
          </ag-grid-vue>
        </div>
      </component>
    </div>
  </div>
</template>

<script>
import {AgGridVue} from 'ag-grid-vue'
import 'ag-grid-enterprise'
import Servers from '../../_servers'
import VueNotifications from 'vue-notifications'
import { mapGetters } from 'vuex'
import PokemonAdd from './PokemonAdd'
import Regions from './_regions'
import Natures from './_natures'
import Monsters from './_monsters'
import iziToast from 'izitoast'
import moment from 'moment'
import {DefaultServerGridOptions} from "@/default_table_settings";
import {applyGridPersistence} from "@/grid_persistence"

export default {
  props: ['records', 'fixedUser', 'lockedServer'],
  components: {
    PokemonAdd,
    'ag-grid-vue': AgGridVue
  },
  data() {
    return {
      gridOptions: null,
      quickFilter: '',
      EnterpriseDatasource: function () {},
      addPokemon: false,
      isUserFiltered: !!this.fixedUser,
      pendingUpdates: {}, // Track { "rowId-field": true }
      successUpdates: {}, // Track successful edits for a few seconds
      errorUpdates: {},   // Track failed edits for a few seconds
      lastUpdateKey: null,
      servers: Servers,
      activeServer: this.lockedServer || Object.keys(Servers)[0],
      defaultColDef: {
        sortable: true,
        resizable: true,
        filter: true,
        filterParams: {
          debounceMs: 500 // Prevent rapid fire requests while typing
        }
      }
    }
  },
  computed: {
    ...mapGetters(['pokemonError', 'allPokemon', 'formMapping'])
  },
  watch: {
    lockedServer(val) {
      if (val && val !== this.activeServer) {
        this.switchServer(val)
      }
    },
                pokemonError(e) {
                  const key = this.lastUpdateKey;

                  if (e.error === 1) {
                    // Success
                    if (key) {
                      this.$set(this.successUpdates, key, true);
                      setTimeout(() => {
                        this.$delete(this.successUpdates, key);
                        if (this.gridOptions.api) this.gridOptions.api.redrawRows();
                      }, 1500);
                    } else {
                      // General Success (Context Menu actions, Add Pokemon, etc.)
                      if (this.addPokemon) {
                        this.showNotification('success', 'Pokemon Added');
                        this.addPokemon = false;
                      } else {
                        this.showNotification('success', 'Operation Successful');
                      }
                      this.refreshGrid();
                    }
                  } else {
                    // Failure: Show 'X' and Revert
                    if (key) {
                      this.$set(this.errorUpdates, key, true);
                      setTimeout(() => {
                        this.$delete(this.errorUpdates, key);
                        this.refreshGrid(); // Revert local change to server state
                      }, 2000);
                    }

                    if (e.error === 2) {
                      iziToast.error({ title: 'Error', message: 'User is Online. Reverting...', position: 'topRight' });
                    } else if (e.error === 10) {
                      this.addPokemon = false;
                      this.showNotification('info', 'Request Sent', 'Forwarded to admin.');
                    } else {
                      iziToast.error({ title: 'Error', message: 'Operation failed. Reverting...', position: 'topRight' });
                    }
                  }

                  // Always clear pending
                  this.pendingUpdates = {};
                  this.lastUpdateKey = null;
                  if (this.gridOptions.api) this.gridOptions.api.redrawRows();
                },    allPokemon(e) {
      if(this.cb) this.cb(e.rows, e.lastRow)
    },
    formMapping() {
      // Refresh columns to show correct form names in filters/dropdowns once mapping data is loaded
      if (this.gridOptions && this.gridOptions.api) {
        this.gridOptions.api.setColumnDefs(this.createColumnDefs());
      }
    }
  },
  beforeMount() {
    this.gridOptions = {
      ...DefaultServerGridOptions,
      rowModelType: 'serverSide',
      animateRows: true,
      rowSelection: 'multiple',
      cacheBlockSize: 50,
      maxBlocksInCache: 10,
      blockLoadDebounceMillis: 500,
      serverSideInfiniteScroll: true,
      getRowId: (params) => params.data.ID,
      columnDefs: this.createColumnDefs(),
      getContextMenuItems: this.getContextMenuItems,
      onFilterChanged: this.onFilterChanged,
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
          { statusPanel: 'agSelectedRowCountComponent', align: 'right' }
        ]
      },
      getRowClass: (params) => {
        if (this.fixedUser && params.data && params.data.Position >= 1 && params.data.Position <= 6) {
          return 'bg-team-member';
        }
      }
    }
    const gridId = this.fixedUser ? 'pokemon-user-grid' : 'pokemon-list-grid'
    applyGridPersistence(this.gridOptions, gridId)
    this.emit('getFormMapping', '')
  },
  methods: {
    // --- Grid Events ---
    onGridReady(params) {
      const self = this;
      this.EnterpriseDatasource.prototype.getRows = (params) => {
        let req = params.request
        req.server = self.activeServer

        // Force Username filter if fixedUser is set
        if (self.fixedUser) {
          req.filterModel = req.filterModel || {};
          req.filterModel.Username = { filterType: 'text', type: 'equals', filter: self.fixedUser };
        }

        // Handle Quick Search (Client-sideish trigger for server filter)
        if (self.quickFilter) {
          req.filterModel = req.filterModel || {};
          req.filterModel.Pokemon = { filterType: 'text', type: 'contains', filter: self.quickFilter };
        }

        self.emit('searchPokemon', req)
        self.cb = (rows, lastRow) => {
          params.successCallback(rows, lastRow);
        }
      }

      // Default Filters
      const filterModel = {};

      // Only show Position >= 0 (Hide deleted/trash) if we are looking at a specific user's team
      // Otherwise, keep it clean for global search
      if (this.fixedUser) {
        filterModel.Position = {
          filterType: 'number',
          type: 'greaterThanOrEqual',
          filter: 0
        };
        filterModel.Username = { filterType: 'text', type: 'equals', filter: this.fixedUser };
      }

      params.api.setFilterModel(filterModel);
      params.api.setServerSideDatasource(new this.EnterpriseDatasource());
    },

    onQuickFilterChanged() {
      // Debounce could be added here
      this.refreshGrid();
    },

    onFilterChanged() {
      const model = this.gridOptions.api.getFilterModel();

      // Strict filters that are indexed and selective enough to allow global sorting/filtering on other columns
      const hasStrictFilter = (model.Username && (model.Username.type === 'equals' || model.Username.type === 'contains')) ||
                              (model.ID && model.ID.type === 'equals') ||
                              (model.GID && model.GID.type === 'equals') ||
                              (model.OT && model.OT.type === 'equals') ||
                              !!this.fixedUser;

      if (this.isUserFiltered !== hasStrictFilter) {
        this.isUserFiltered = hasStrictFilter;
        this.safeUpdateColumnDefs();
      }
    },

    refreshGrid() {
      if(this.gridOptions.api) {
        this.gridOptions.api.refreshServerSide();
        this.gridOptions.api.deselectAll();
      }
    },

    // --- Helper for stable column updates ---
    safeUpdateColumnDefs() {
      if (!this.gridOptions || !this.gridOptions.columnApi) return;

      // 1. Capture current user layout
      const currentState = this.gridOptions.columnApi.getColumnState();

      // 2. Apply new definitions
      this.gridOptions.api.setColumnDefs(this.createColumnDefs());

      // 3. Restore layout after a tiny delay to let Ag-Grid process the new cols
      setTimeout(() => {
        this.gridOptions.columnApi.applyColumnState({
          state: currentState,
          applyOrder: true
        });
        this.gridOptions.api.refreshHeader();
      }, 50);
    },

    // --- Column Definitions ---
    createColumnDefs() {
      const self = this;

      // Helper for Permission checks
      const can = (perm) => self.hasPermission(perm);
      const isUserMode = this.isUserFiltered;

      // Logic for cell loading state
      const isUpdating = (field) => (params) => {
        if (!params.data) return false;
        return !!this.pendingUpdates[`${params.data.ID}-${field}`];
      };

      const loadingRule = (field) => ({ 'cell-loading': isUpdating(field) });
      const successRule = (field) => ({ 'cell-success': (params) => params.data && !!this.successUpdates[`${params.data.ID}-${field}`] });
      const errorRule = (field) => ({ 'cell-error': (params) => params.data && !!this.errorUpdates[`${params.data.ID}-${field}`] });

      const statusRules = (field) => ({
        ...loadingRule(field),
        ...successRule(field),
        ...errorRule(field)
      });

      const staffRewardMapping = {
        0: 'Tradable',
        1: 'Legacy Trade-Lock',
        2: '6th-month staff reward',
        3: '8th-month staff reward',
        4: 'Tournament reward',
        5: 'Temporarily script-spawned',
        6: 'Permanently script-spawned'
      };

      const formMapping = this.formMapping.names || {};
      const speciesMappings = this.formMapping.mappings || {};

      return [
        // --- Group: Identity ---
        {
          headerName: "Identity",
          children: [
            {
              headerName: 'ID', field: 'ID', width: 90, pinned: 'left',
              filter: 'agNumberColumnFilter',
              checkboxSelection: true,
              sortable: true
            },
            {
              headerName: 'GID', field: 'GID', width: 90, pinned: 'left',
              filter: 'agNumberColumnFilter',
              sortable: true
            },
            {
              headerName: 'Pokemon', field: 'Pokemon', width: 140, pinned: 'left',
              cellRenderer: params => `<strong>${params.value}</strong>`,
              filter: 'agTextColumnFilter',
              sortable: true
            },
            {
              headerName: 'Position',
              field: 'Position',
              width: 120,
              pinned: 'left',
              filter: isUserMode ? 'agNumberColumnFilter' : false,
              filterParams: {
                suppressAndOrCondition: true,
                filterOptions: ['equals', 'notEqual', 'lessThan', 'greaterThan', 'greaterThanOrEqual']
              },
              cellRenderer: params => {
                const val = params.value;
                if (self.fixedUser && val >= 1 && val <= 6) {
                  return `<span class="badge badge-primary"><i class="fa fa-star"></i> Team ${val}</span>`;
                }
                if (val >= 0) return val;
                const reasons = {
                  '-1': { text: 'User Release', class: 'badge-danger' },
                  '-2': { text: 'Script Release', class: 'badge-warning' },
                  '-3': { text: 'Reserved', class: 'badge-secondary' },
                  '-4': { text: 'Lending', class: 'badge-info' },
                  '-5': { text: 'Returned', class: 'badge-success' }
                };
                const reason = reasons[val];
                return reason ? `<span class="badge ${reason.class}">${reason.text}</span>` : `<span class="badge badge-secondary">Deleted (${val})</span>`;
              },
              sortable: isUserMode,
              sort: self.fixedUser ? 'asc' : null
            },
            {
              headerName: 'Owner', field: 'Username', width: 120, pinned: 'left',
              hide: !!this.fixedUser,
              editable: can(self.PokemonOwnerTransfer),
              onCellValueChanged: self.onOwnerChange,
              filter: 'agTextColumnFilter',
              cellClassRules: statusRules('Username'),
              sortable: true
            }
          ]
        },

        // --- Group: Traits ---
        {
          headerName: "Traits",
          children: [
            {
              headerName: 'Form',
              field: 'FormName',
              width: 130,
              filter: isUserMode ? 'agSetColumnFilter' : 'agTextColumnFilter',
              cellClassRules: statusRules('FormName'),
              filterParams: {
                values: (params) => {
                  const names = this.formMapping && this.formMapping.names ? Object.values(this.formMapping.names) : [];
                  const uniqueNames = [...new Set(['Normal', ...names])].sort();
                  params.success(uniqueNames);
                },
                suppressAndOrCondition: true,
                comparator: (a, b) => {
                  if (a === 'Normal') return -1;
                  if (b === 'Normal') return 1;
                  return a.localeCompare(b);
                }
              },
              valueFormatter: params => {
                if (!params.data) return '';
                const val = params.data.Form;
                if (val === 0) return 'Normal';

                const monsterId = params.data.MonsterID;
                if (speciesMappings[monsterId]) {
                  const f = speciesMappings[monsterId].find(x => x.id === val);
                  if (f) {
                    const duplicates = speciesMappings[monsterId].filter(x => x.name === f.name).length;
                    if (duplicates > 1) {
                      const idx = speciesMappings[monsterId].filter(x => x.name === f.name).findIndex(x => x.id === f.id) + 1;
                      return `${f.name} (${idx})`;
                    }
                    return f.name;
                  }
                }
                return params.value || 'Normal';
              },
              valueSetter: params => {
                if (params.newValue === params.oldValue) return false;
                params.data.Form = Number(params.newValue);
                params.data.FormName = params.newValue;
                return true;
              },
              editable: can(self.PokemonEditForm),
              onCellValueChanged: self.onFormChange,
              cellEditor: 'agRichSelectCellEditor',
              cellEditorParams: params => {
                const monsterId = params.data.MonsterID;
                const options = [{ id: 0, label: 'Normal' }];

                if (speciesMappings[monsterId]) {
                  speciesMappings[monsterId].forEach(f => {
                    const duplicates = speciesMappings[monsterId].filter(x => x.name === f.name).length;
                    let label = f.name;
                    if (duplicates > 1) {
                      const idx = speciesMappings[monsterId].filter(x => x.name === f.name).findIndex(x => x.id === f.id) + 1;
                      label = `${f.name} (${idx})`;
                    }
                    options.push({ id: f.id, label: label });
                  });
                }

                options.sort((a, b) => {
                  if (a.id === 0) return -1;
                  if (b.id === 0) return 1;
                  return a.label.localeCompare(b.label);
                });

                return {
                  values: options.map(o => o.id),
                  cellRenderer: p => {
                    const opt = options.find(o => o.id === p.value);
                    return opt ? opt.label : (p.value === 0 ? 'Normal' : p.value);
                  }
                };
              },
              sortable: isUserMode
            },
            {
              headerName: 'Gender', field: 'Gender', width: 85,
              filter: isUserMode ? 'agSetColumnFilter' : 'agTextColumnFilter',
              filterParams: {
                values: (params) => params.success(['M', 'F', '-']),
                suppressAndOrCondition: true
              },
              cellRenderer: params => {
                if (params.value === 'M') return '<i class="fa fa-mars text-info" title="Male"></i> M';
                if (params.value === 'F') return '<i class="fa fa-venus text-danger" title="Female"></i> F';
                return '<span class="text-muted">-</span>';
              },
              cellStyle: { textAlign: 'center' },
              sortable: isUserMode
            },
            {
              headerName: 'Shiny', field: 'Special', width: 70,
              filter: isUserMode ? 'agNumberColumnFilter' : false,
              cellRenderer: params => params.value ? '<i class="fa fa-star"></i>' : '',
              cellStyle: { textAlign: 'center' },
              sortable: isUserMode
            },
            {
              headerName: 'Lvl', field: 'LVL', width: 60,
              filter: isUserMode ? 'agNumberColumnFilter' : false,
              cellStyle: { textAlign: 'center' },
              sortable: isUserMode
            }
          ]
        },

        // --- Group: Battle Specs ---
        {
          headerName: "Battle Specs",
          children: [
            {
              headerName: 'Nature', field: 'Nature', width: 90,
              filter: isUserMode ? 'agTextColumnFilter' : false,
              cellEditor: 'agRichSelectCellEditor',
              cellEditorParams: { values: Natures },
              onCellValueChanged: self.onNatureChange,
              editable: can(self.PokemonEditNature),
              cellClassRules: statusRules('Nature'),
              sortable: isUserMode
            },
            {
              headerName: 'Ability', field: 'Ability', width: 110,
              filter: isUserMode ? 'agTextColumnFilter' : false,
              editable: can(self.PokemonEditAbility),
              onCellValueChanged: self.onAbilityChange,
              cellEditor: 'agRichSelectCellEditor',
              cellClassRules: statusRules('Ability'),
              cellEditorParams: params => ({
                values: self.getPokemonfromMonsters(params.data.Pokemon).Ability.filter(el => el !== 'None')
              }),
              sortable: isUserMode
            },
            {
              headerName: 'Item', field: 'Item', width: 100,
              filter: isUserMode ? 'agTextColumnFilter' : false,
              sortable: isUserMode
            }
          ]
        },

        // --- Group: IVs (Individual Values) ---
        {
          headerName: "IVs (Individual Values)",
          headerClass: 'text-center',
          children: [
            ...['HP', 'ATK', 'DEF', 'SPATK', 'SPDEF', 'SPD'].map(stat => ({
              headerName: stat,
              field: `IV_${stat}`,
              width: 65,
              editable: can(self.PokemonEditIVS),
              onCellValueChanged: self.onIVSChange,
              filter: (isUserMode || ['SPD', 'SPATK', 'ATK'].includes(stat)) ? 'agNumberColumnFilter' : false,
              cellClassRules: statusRules(`IV_${stat}`),
              cellStyle: params => {
                if (params.value > 19) return { color: '#4dbd74', fontWeight: 'bold' };
                if (params.value < 10) return { color: '#f86c6b' };
                return null;
              },
              sortable: isUserMode || ['SPD', 'SPATK', 'ATK'].includes(stat) // Only these have indexes in the database
            }))
          ]
        },

        // --- Group: EVs (Effort Values) ---
        {
          headerName: "EVs (Effort Values)",
          headerClass: 'text-center',
          children: [
            {
              headerName: 'Total',
              width: 80,
              hide: true,
              valueGetter: params => {
                if(!params.data) return 0;
                return (params.data.EVHP || 0) + (params.data.EVATK || 0) + (params.data.EVDEF || 0) +
                  (params.data.EVSPD || 0) + (params.data.EVSPATK || 0) + (params.data.EVSPDEF || 0);
              },
              filter: isUserMode,
              sortable: isUserMode
            },
            ...['HP', 'ATK', 'DEF', 'SPATK', 'SPDEF', 'SPD'].map(stat => ({
              headerName: stat,
              field: `EV${stat}`,
              width: 65,
              hide: true,
              filter: isUserMode ? 'agNumberColumnFilter' : false,
              sortable: isUserMode
            }))
          ]
        },

        // --- Group: Origin & Status ---
        {
          headerName: "Origin & Status",
          children: [
            {
              headerName: 'OT', field: 'OT', width: 100,
              filter: 'agTextColumnFilter',
              editable: can(self.PokemonEditOT),
              onCellValueChanged: self.onOTChange,
              cellClassRules: statusRules('OT'),
              sortable: true
            },
            {
              headerName: 'Caught', field: 'CaughtDate', width: 160,
              filter: isUserMode ? 'agDateColumnFilter' : 'agTextColumnFilter',
              cellRenderer: params => self.formatDateDisplay(params.value),
              tooltipValueGetter: params => {
                if (!params.value) return '';
                const m = moment.utc(params.value);
                return `UTC: ${m.format('YYYY-MM-DD HH:mm:ss')} | Local: ${m.local().format('YYYY-MM-DD HH:mm:ss')}`;
              },
              sortable: isUserMode
            },
            {
              headerName: 'Released', field: 'release_date', colId: 'ReleaseDate', width: 160,
              filter: isUserMode ? 'agDateColumnFilter' : 'agTextColumnFilter',
              cellRenderer: params => self.formatDateDisplay(params.value),
              hide: true, // Hidden by default
              valueGetter: params => {
                // If it's a released pokemon, make sure the column becomes visible if not already
                if (params.data && params.data.Position < 0 && params.column.isVisible() === false) {
                   setTimeout(() => params.columnApi.setColumnVisible('ReleaseDate', true), 0);
                }
                return params.data ? params.data.release_date : null;
              },
              sortable: isUserMode
            },
            {
              headerName: 'Region', field: 'CaughtRegion', width: 110,
              filter: isUserMode ? 'agSetColumnFilter' : 'agTextColumnFilter',
              filterParams: {
                values: (params) => {
                  const names = Regions.map(r => r);
                  params.success(names);
                },
                valueFormatter: params => params.value,
                suppressAndOrCondition: true
              },
              valueGetter: params => {
                if (!params.data) return null;
                return Regions[params.data.CaughtRegion - 1] || 'Unknown';
              },
              cellRenderer: params => {
                const region = params.value;
                return region ? `<span class="badge badge-secondary">${region}</span>` : '';
              },
              sortable: isUserMode
            },
            ...(!['red', 'blue'].includes(self.activeServer.toLowerCase()) ? [{
              headerName: 'Trade Lock', field: 'StaffReward', width: 180,
              filter: isUserMode ? 'agNumberColumnFilter' : false,
              editable: can(self.PokemonEditTradeLock),
              onCellValueChanged: self.onStaffRewardChange,
              cellClassRules: statusRules('StaffReward'),
              cellRenderer: params => {
                const val = params.value || 0;
                const label = staffRewardMapping[val] || `Unknown (${val})`;
                const isLocked = val > 0;
                return `<span class="badge badge-${isLocked ? 'danger' : 'success'}">${label}</span>`;
              },
              cellEditor: 'agRichSelectCellEditor',
              cellEditorParams: {
                values: Object.keys(staffRewardMapping).map(Number),
                cellRenderer: params => staffRewardMapping[params.value]
              },
              sortable: isUserMode
            }] : [])
          ]
        }
      ];
    },

    // --- Actions ---
    getContextMenuItems(params) {
      if (!params.node || !params.node.data) {
        return ['export'];
      }
      const self = this;
      const selected = params.api.getSelectedRows();
      if (!selected.find(r => r.ID === params.node.data.ID)) selected.push(params.node.data);

      const isReadOnly = self.servers[self.activeServer].readonly;

      return [
        {
          name: 'Copy Details',
          icon: '<i class="fa fa-clipboard"/>',
          action: () => {
            const d = params.node.data;
            const text = `ID: ${d.ID} | ${d.Pokemon} (Lvl ${d.LVL}) | IVs: ${d.IV_HP}/${d.IV_ATK}/${d.IV_DEF}/${d.IV_SPATK}/${d.IV_SPDEF}/${d.IV_SPD}`;
            navigator.clipboard.writeText(text);
            iziToast.success({ title: 'Copied', message: 'Pokemon details copied to clipboard' });
          }
        },
        'separator',
        {
          name: 'Transfer Owner',
          disabled: isReadOnly || !self.hasPermission(self.PokemonOwnerTransfer),
          icon: '<i class="fa fa-exchange"/>',
          action: () => {
            const newOwner = prompt("Please enter the new owner's username:");
            if (newOwner && newOwner.trim() !== '') {
              self.performAction('transferPokemon', selected, newOwner.trim());
            }
          }
        },
        {
          name: 'Recover',
          disabled: isReadOnly || !self.hasPermission(self.PokemonRecover) || params.node.data.Position !== -1,
          icon: '<i class="fa fa-undo"/>',
          action: () => self.performAction('recoverPokemon', selected, params.node.data.Username)
        },
        {
          name: 'Delevel',
          disabled: isReadOnly || params.node.data.LVL <= 1 || !self.hasPermission(self.PokemonDelevel),
          icon: '<i class="fa fa-level-down"/>',
          action: () => self.emit('delevelPokemon', { id: params.node.data.ID, value: params.node.data.Username, server: self.activeServer })
        },
        {
          name: 'Delete',
          disabled: isReadOnly || params.node.data.Position < 1 || !self.hasPermission(self.PokemonDelete),
          icon: '<i class="fa fa-trash text-danger"/>',
          action: () => self.emit('deletePokemon', { id: params.node.data.ID, value: params.node.data.Username, server: self.activeServer })
        },
        'separator',
        'export'
      ];
    },

    // --- Logic Helpers ---
    performAction(actionName, selectedRows, value) {
      const ids = selectedRows.map(r => r.ID);
      this.emit(actionName, { id: ids, value: value, server: this.activeServer });
    },

    getPokemonfromMonsters(name) {
      return Monsters.find(m => m.Name === name) || { Ability: [] };
    },

    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },

    switchServer(index) {
      this.activeServer = index;
      if(this.gridOptions.api) {
        this.safeUpdateColumnDefs();
        this.gridOptions.api.setServerSideDatasource(new this.EnterpriseDatasource());
      }
    },

    showNotification(type, title, message = '') {
      // Wrapper for iziToast or VueNotifications
      if (type === 'error') iziToast.error({ title, message });
      else if (type === 'success') iziToast.success({ title, message });
      else iziToast.info({ title, message });
    },

    // --- Change Handlers (Simplified) ---
    onAbilityChange(p) {
      if(p.newValue !== p.oldValue) {
        this.emitChange(p.data.ID, 'Ability', this.getAbilityIndex(p.data.Pokemon, p.newValue));
      }
    },
    onNatureChange(p) {
      if(p.newValue !== p.oldValue) {
        this.emitChange(p.data.ID, 'Nature', Natures.indexOf(p.newValue));
      }
    },
    onFormChange(p) {
      if(p.newValue !== p.oldValue && p.newValue >= 0) {
        // p.newValue is already the ID because of how cellEditorParams.values is set
        this.emitChange(p.data.ID, 'Form', Number(p.newValue), 'FormName');
      }
    },
    onOTChange(p) {
      if(p.newValue !== p.oldValue) {
        this.emitChange(p.data.ID, 'OT', p.newValue);
      }
    },
    onStaffRewardChange(p) {
      if(p.newValue !== p.oldValue) {
        this.emitChange(p.data.ID, 'StaffReward', Number(p.newValue));
      }
    },
    onTradelockChange(p) {
      if(p.newValue !== p.oldValue) {
        this.emitChange(p.data.ID, 'Tradelock', p.newValue);
      }
    },

    onIVSChange(params) {
      if (params.oldValue === params.newValue) return;
      const iv = Number(params.newValue);
      if (iv > 31 || iv < 0) {
        this.showNotification('error', 'Invalid IV', 'Must be 0-31');
        params.node.setDataValue(params.column.colId, params.oldValue);
        return;
      }
      const d = params.data;
      const newIVs = [Number(d.IV_ATK), Number(d.IV_DEF), Number(d.IV_SPD), Number(d.IV_SPATK), Number(d.IV_SPDEF), Number(d.IV_HP)];
      this.emitChange(d.ID, 'IVS', newIVs, params.column.colId);
    },

    onOwnerChange(params) {
      if(params.oldValue === params.newValue) return;
      const newOwner = (params.newValue || '').trim();
      this.emitOwnerTransfer(params.data.ID, newOwner);
    },

    getAbilityIndex(pokemon, abilityName) {
      const m = this.getPokemonfromMonsters(pokemon);
      return m.Ability ? m.Ability.indexOf(abilityName) + 1 : 0;
    },

    emitOwnerTransfer(id, newOwner) {
      const field = 'Username';
      this.lastUpdateKey = `${id}-${field}`;
      this.$set(this.pendingUpdates, this.lastUpdateKey, true);
      this.gridOptions.api.redrawRows();
      this.emit('transferPokemon', { id: [id], value: newOwner, server: this.activeServer });
    },

    emitChange(id, key, value, trackField) {
      const field = trackField || key;
      this.lastUpdateKey = `${id}-${field}`;
      this.$set(this.pendingUpdates, this.lastUpdateKey, true);
      this.gridOptions.api.redrawRows();
      this.emit('editPokemon', { id, key, value, server: this.activeServer });
    }
  }
}
</script>

<style scoped>
.ag-theme-balham-dark {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
.ag-theme-balham-dark >>> .bg-team-member {
  background-color: rgba(32, 168, 216, 0.15) !important;
}
.bg-dark-subtle {
  background-color: rgba(0, 0, 0, 0.2) !important;
}
.ag-theme-balham-dark >>> .cell-loading {
  position: relative;
  pointer-events: none;
  background-color: rgba(32, 168, 216, 0.08) !important;
}
.ag-theme-balham-dark >>> .cell-loading::after {
  content: "";
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  border: 2px solid rgba(32, 168, 216, 0.2);
  border-top-color: #20a8d8;
  border-radius: 50%;
  animation: smooth-spin 0.8s infinite linear;
}
@keyframes smooth-spin {
  to { transform: translateY(-50%) rotate(360deg); }
}
.ag-theme-balham-dark >>> .cell-success {
  background-color: rgba(77, 189, 116, 0.1) !important;
  animation: bg-fade-out 1.5s forwards;
}
.ag-theme-balham-dark >>> .cell-success::after {
  content: "\f00c"; /* fa-check */
  font-family: FontAwesome;
  position: absolute;
  right: 8px;
  top: 50%;
  color: #4dbd74;
  font-size: 11px;
  line-height: 1;
  transform: translateY(-50%);
  animation: check-in 0.3s ease-out forwards, fade-out 0.5s 1s forwards;
}
@keyframes check-in {
  0% { transform: translateY(-50%) scale(0); opacity: 0; }
  70% { transform: translateY(-50%) scale(1.2); }
  100% { transform: translateY(-50%) scale(1); opacity: 1; }
}
.ag-theme-balham-dark >>> .cell-error {
  background-color: rgba(248, 108, 107, 0.15) !important;
  animation: bg-fade-out 2s forwards;
}
.ag-theme-balham-dark >>> .cell-error::after {
  content: "\f00d"; /* fa-times */
  font-family: FontAwesome;
  position: absolute;
  right: 8px;
  top: 50%;
  color: #f86c6b;
  font-size: 11px;
  line-height: 1;
  transform: translateY(-50%);
  animation: check-in 0.3s ease-out forwards, fade-out 0.5s 1.5s forwards;
}
@keyframes fade-out {
  from { opacity: 1; }
  to { opacity: 0; }
}
@keyframes bg-fade-out {
  from { }
  to { background-color: transparent !important; }
}
</style>
